import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Loader2, Package, RotateCw, Plus, ChevronRight } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { canManageInventory } from '../../lib/role.js'
import { usePersistedState } from '../../lib/persist.js'
import { fetchInventoryMaster, imageUrl, byCategory } from '../../lib/stock.js'
import InventoryEditDrawer from '../../components/stock/InventoryEditDrawer.jsx'
import { itemName, listLevel } from '../../lib/inventory.js'

// คลังของ — the item master. This page is the INDEX; the numbers live one click
// deeper (InventoryDetail), because the per-item stats each cost their own
// aggregate call and 450 of them would be a page that never finishes loading.
//
// A TABLE, not cards: the job here is scanning and comparing ~450 rows down a
// column (name, category, unit, level), which a grid of cards makes you read in
// a zig-zag. Cards were the wrong default for a list this long.
//
// The master is pulled ONCE (paged type=normal) and filtered client-side, so
// search and both category filters are instant — same reasoning as ตัด Stock.

export default function InventoryList() {
  const navigate = useNavigate()
  const me = useMe()
  const canEdit = canManageInventory(me)

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  const [q, setQ] = useState('')
  const [cat, setCat] = usePersistedState('inv:cat', '')
  const [sub, setSub] = usePersistedState('inv:sub', '')
  const [addOpen, setAddOpen] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchInventoryMaster(apiGet)
      .then((list) => { if (alive) { setItems(list); setError(null) } })
      .catch((e) => { if (alive) setError(e.message || 'โหลดรายการของไม่สำเร็จ') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [reloadKey])

  const categories = useMemo(() => [...byCategory(items).keys()], [items])

  // Subcategories are scoped to the selected main category — showing every
  // subcategory in the master would offer combinations that match nothing.
  const subCategories = useMemo(() => {
    const pool = cat ? items.filter((it) => (it.mainCategory || 'อื่นๆ') === cat) : items
    return [...new Set(pool.map((it) => (it.subCategory || '').trim()).filter(Boolean))].sort()
  }, [items, cat])

  // A persisted subcategory that no longer exists under the newly-chosen main
  // category would filter everything away with no visible cause.
  useEffect(() => {
    if (sub && !subCategories.includes(sub)) setSub('')
  }, [subCategories, sub, setSub])

  const allSubCategories = useMemo(
    () => [...new Set(items.map((it) => (it.subCategory || '').trim()).filter(Boolean))].sort(), [items])
  const allUnits = useMemo(
    () => [...new Set(items.map((it) => (it.unit || '').trim()).filter(Boolean))].sort(), [items])

  // Search matches the NAME ONLY (per user, 2026-07-18). Categories have their
  // own dropdowns, so folding them into the text box made the two disagree:
  // typing "Dental" returned every item in that category as if it were a name
  // match, burying the item actually being looked for.
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return items.filter((it) => {
      if (cat && (it.mainCategory || 'อื่นๆ') !== cat) return false
      if (sub && (it.subCategory || '').trim() !== sub) return false
      if (!needle) return true
      return String(it.Name || '').toLowerCase().includes(needle)
    })
  }, [items, q, cat, sub])

  const filtered = !!(q || cat || sub)

  return (
    <div className="px-10 py-8 max-w-[1150px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 tracking-tight mb-1">คลังของ</h1>
          <p className="text-[14px] text-slate-500">รายการของทั้งหมด — เลือกของเพื่อดูยอดคงเหลือ สถิติการเบิกใช้ และแก้ไขข้อมูล</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setReloadKey((k) => k + 1)} title="โหลดใหม่"
            className="rounded-lg border border-slate-200 bg-white p-2 text-slate-500 hover:text-brand-600 hover:border-brand-300">
            <RotateCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          {canEdit && (
            <button onClick={() => setAddOpen(true)}
              className="rounded-xl bg-brand-600 px-4 py-2.5 text-[14px] font-semibold text-white hover:bg-brand-700 flex items-center gap-2">
              <Plus size={16} />เพิ่มของใหม่
            </button>
          )}
        </div>
      </div>

      {/* Filters — one row: search, then main category, then subcategory. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="ค้นหาชื่อของ…"
            className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10 py-2.5 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none" />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X size={16} />
            </button>
          )}
        </div>

        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">หมวดหมู่</span>
          <select value={cat} onChange={(e) => { setCat(e.target.value); setSub('') }}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 max-w-[190px]">
            <option value="">ทั้งหมด</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <span className="text-slate-400">หมวดย่อย</span>
          <select value={sub} onChange={(e) => setSub(e.target.value)} disabled={!subCategories.length}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 max-w-[190px] disabled:opacity-50">
            <option value="">ทั้งหมด</option>
            {subCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        {filtered && (
          <button onClick={() => { setQ(''); setCat(''); setSub('') }}
            className="text-[13px] text-slate-500 hover:text-brand-600 flex items-center gap-1">
            <X size={14} />ล้างตัวกรอง
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11.5px] font-medium uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-5 py-3 font-medium w-[52px]" />
                <th className="px-2 py-3 font-medium">ชื่อของ</th>
                <th className="px-5 py-3 font-medium">หมวดหมู่</th>
                <th className="px-5 py-3 font-medium">หมวดย่อย</th>
                <th className="px-5 py-3 font-medium">หน่วย</th>
                <th className="px-5 py-3 font-medium text-right whitespace-nowrap">คงเหลือ</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={7} className="px-5 py-16 text-center text-slate-400">
                  <Loader2 size={18} className="inline animate-spin mr-2" /> กำลังโหลดรายการของ…
                </td></tr>
              )}
              {!loading && !visible.length && (
                <tr><td colSpan={7} className="px-5 py-16 text-center text-[14px] text-slate-400">
                  {filtered ? 'ไม่พบของที่ตรงกับตัวกรอง' : 'ยังไม่มีของในคลัง'}
                </td></tr>
              )}
              {!loading && visible.map((it) => {
                const img = imageUrl(it)
                // listLevel returns null when the addon's shape is unrecognised —
                // render "—" rather than a confident 0, which would read as
                // "we are out of it". The detail page has the real number.
                const level = listLevel(it)
                const inactive = it.active === false
                return (
                  <tr key={it.id} onClick={() => navigate(`/stock/items/${it.id}`)}
                    className="group hover:bg-slate-50/60 cursor-pointer">
                    <td className="px-5 py-2.5">
                      <div className="w-9 h-9 rounded-lg border border-slate-100 bg-slate-50 overflow-hidden flex items-center justify-center">
                        {img
                          ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                          : <Package size={15} className="text-slate-300" />}
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <div className={`text-[13.5px] font-medium ${inactive ? 'text-slate-400' : 'text-slate-800'}`}>
                        {itemName(it)}
                        {inactive && (
                          <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-normal text-slate-500">
                            ปิดใช้งาน
                          </span>
                        )}
                      </div>
                      {it.note && <div className="text-[11.5px] text-slate-400 truncate max-w-[320px]">{it.note}</div>}
                    </td>
                    <td className="px-5 py-2.5 text-slate-600">{it.mainCategory || '—'}</td>
                    <td className="px-5 py-2.5 text-slate-500">{it.subCategory || '—'}</td>
                    <td className="px-5 py-2.5 text-slate-500 whitespace-nowrap">{it.unit || '—'}</td>
                    <td className={`px-5 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap ${
                      level == null ? 'text-slate-300' : level < 0 ? 'text-rose-600' : 'text-slate-800'
                    }`}>
                      {level == null ? '—' : level}
                    </td>
                    <td className="px-3 py-2.5 text-slate-300">
                      <ChevronRight size={15} className="group-hover:text-brand-400" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {!loading && (
          <div className="border-t border-slate-100 px-5 py-3 text-[13px] text-slate-500">
            {filtered
              ? <>แสดง <b className="text-slate-700">{visible.length}</b> จาก {items.length} รายการ</>
              : <>ทั้งหมด <b className="text-slate-700">{items.length}</b> รายการ</>}
          </div>
        )}
      </div>

      {addOpen && (
        <InventoryEditDrawer
          item={null}
          categoryOptions={categories}
          subCategoryOptions={allSubCategories}
          unitOptions={allUnits}
          onClose={() => setAddOpen(false)}
          // Land on the new item's own page when Xano hands back an id — the
          // next thing anyone does with a just-created item is add its picture
          // or record the stock it arrived with. Otherwise just refresh.
          onSaved={(created) => {
            const newId = Number(created?.id ?? created?.inventory?.id) || 0
            if (newId) navigate(`/stock/items/${newId}`)
            else setReloadKey((k) => k + 1)
          }}
        />
      )}
    </div>
  )
}
